import type { Gate } from '../entities/CourseActors';
import type { RivalBoat } from '../entities/RivalBoat';

// Circular minimap: full course gates, live boats, rotating with player heading.
export class Minimap {
  private readonly ctx: CanvasRenderingContext2D | null;
  private readonly canvas: HTMLCanvasElement;
  private readonly worldRadius: number;

  constructor(
    selector: string,
    worldRadius = 190,
  ) {
    const canvas = document.querySelector<HTMLCanvasElement>(selector);
    if (!canvas) throw new Error(`Missing minimap element: ${selector}`);
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d');
    this.worldRadius = worldRadius;
  }

  update(
    playerX: number,
    playerZ: number,
    heading: number,
    gates: Gate[],
    currentGate: number,
    rivals: RivalBoat[],
    finished: boolean,
  ): void {
    const ctx = this.ctx;
    if (!ctx) return;
    const w = this.canvas.width;
    const h = this.canvas.height;
    const cx = w / 2;
    const cy = h / 2;
    const scale = (w / 2 - 10) / this.worldRadius;

    ctx.clearRect(0, 0, w, h);

    // Rotate so player heading is up
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(heading);

    // Gates: course polyline + nodes
    ctx.strokeStyle = 'rgba(125, 255, 179, 0.28)';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    gates.forEach((gate, i) => {
      const gx = (gate.position.x - playerX) * scale;
      const gz = (gate.position.y - playerZ) * scale;
      if (i === 0) ctx.moveTo(gx, -gz);
      else ctx.lineTo(gx, -gz);
    });
    ctx.closePath();
    ctx.stroke();

    for (let i = 0; i < gates.length; i++) {
      const gx = (gates[i].position.x - playerX) * scale;
      const gz = (gates[i].position.y - playerZ) * scale;
      const isNext = i === currentGate && !finished;
      ctx.beginPath();
      ctx.arc(gx, -gz, isNext ? 4.5 : 2.5, 0, Math.PI * 2);
      ctx.fillStyle = isNext ? '#7dffb3' : 'rgba(125, 255, 179, 0.4)';
      ctx.fill();
    }

    // Rivals
    for (const rival of rivals) {
      const rx = (rival.position.x - playerX) * scale;
      const rz = (rival.position.z - playerZ) * scale;
      ctx.beginPath();
      ctx.arc(rx, -rz, 3, 0, Math.PI * 2);
      ctx.fillStyle = '#fbbf24';
      ctx.fill();
    }

    ctx.restore();

    // Player: centered, pointing up
    ctx.fillStyle = '#f24d3d';
    ctx.beginPath();
    ctx.moveTo(cx, cy - 5);
    ctx.lineTo(cx - 4, cy + 4);
    ctx.lineTo(cx + 4, cy + 4);
    ctx.closePath();
    ctx.fill();
  }
}
